import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  ThemeProvider,
  Typography
} from '@mui/material';
import MobiledataOffIcon from '@mui/icons-material/MobiledataOff';
import LineChart from '../../components/Charts/LineChart/LineChart';
import KpiGrid from '../../components/KpiGrid/KpiGrid';
import CustomTabs from '../../components/CustomTabs/CustomTabs';
import BarChart from '../../components/Charts/BarChart/BarChart';
import {
  fetchCurrentTimeFrame,
  fetchLineChartData,
  getDropdownListByFilterParam,
  getUserActionSummary,
  getWaterfallChartData,
  fetchPortfolioHealthKPIAndChartsData,
  fetchBarChartData
} from './services';
import { setPortfolioSelectedFiltersData } from '../../redux/PortfolioHealth/portfolioHealthSlice';
import { useDispatch } from 'react-redux';
import { Card } from 'pepsico-ds';
import MultiSelectFilters from './MultiSelectFilters';
import { createTheme } from '@mui/material/styles';
import PageHeader from '../../components/PageHeader/PageHeader';
import WaterfallChart from '../../components/Charts/WaterfallChart/WaterfallChart';
import { fetchCycleTriggerData } from '../CycleTrigger/services';
import { debounce } from 'lodash';
import { isInvalidValue } from '../../utils/Utils';
import { useOkta } from '../../hooks/oktaHook';
import { triggerNotification } from '../../utils/NotificationContext';
import SkuDetails from './SkuDetails';

const theme = createTheme({
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '14px'
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '14px'
        }
      }
    }
  }
});

const timeFrameOptions = ['L13', 'YTD'];

const getKeyByValue = (object, value) => {
  return Object.keys(object).find((key) => object[key] === value);
};

const chartTabs = [
  { id: 'Segment', name: 'Segment' },
  { id: 'SubCategory', name: 'Sub Category' },
  { id: 'Brand', name: 'Brand' },
  { id: 'Region', name: 'Region' },
  { id: 'Channel', name: 'Channel' }
];

const trendAnalysisOptions = ['Revenue', 'Revenue per SKU', 'Profit', 'Profit %', 'Waste %'];
const performanceBreakdownOptions = ['Revenue', 'Profit'];

const trendDropdownMapping = {
  Revenue: 'Revenue',
  RevenuePerSku: 'Revenue per SKU',
  Profit: 'Profit',
  Profitpercentage: 'Profit %',
  Wastepercentage: 'Waste %'
};

const performanceBreakdownMapping = {
  Revenue: 'Revenue',
  Profit: 'Profit'
};

function PortfolioHealth() {
  const dispatch = useDispatch();
  const { userDetails } = useOkta();
  const { roles: userRole, username: userName } = userDetails;
  const [lineChartData, setLineChartData] = useState({});
  const [barChartData, setBarChartData] = useState({});
  const [actualVsPreviousKPIData, setActualVsPreviousKPIData] = useState([]);
  const [selectedTab, setSelectedTab] = useState('Brand');
  const [selectedTrendAnalysis, setSelectedTrendAnalysis] = useState('Revenue');
  const [selectedPerformanceBreakdownFilter, setSelectedPerformanceBreakdownFilter] =
    useState('Revenue');
  const [valueLabel, setValueLabel] = useState(selectedTrendAnalysis);
  const [performanceBreakdownLabel, setPerformanceBreakdownLabel] = useState(
    selectedPerformanceBreakdownFilter
  );
  const [isKPICardLoading, setIsKPICardLoading] = useState(false);
  const [isLineChartLoading, setIsLineChartLoading] = useState(false);
  const [isBarChartLoading, setIsBarChartLoading] = useState(false);
  const [isWaterfallChartLoading, setIsWaterfallChartLoading] = useState(false);
  const [waterfallChartData, setWaterfallChartData] = useState({});
  const [headerFilterData, setHeaderFilterData] = useState([]);
  const [cycleFilterData, setCycleFilterData] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [timeframe, setTimeFrame] = useState({
    startTimePeriod: '',
    endTimePeriod: '',
    cycleName: ''
  });
  const [cyclesData, setCyclesData] = useState([]);

  const { search } = useLocation();
  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const selectedBu = newParams.get('bu');

  const [activeCycleData, setActiveCycleData] = useState([]);
  const [activeCycleId, setActiveCycleId] = useState(selectedCycleId || null);
  const [timeFrameValue, setTimeFrameValue] = useState(timeFrameOptions[0]);
  const [showLoader, setShowLoader] = useState(false);

  const params = {
    trendAnalysisBrekup: getKeyByValue(trendDropdownMapping, selectedTrendAnalysis) || 'Revenue',
    performanceBreakdown:
      getKeyByValue(performanceBreakdownMapping, selectedPerformanceBreakdownFilter) || 'Revenue',
    // CurrentCycleName: "",
    levelName: selectedTab,
    buName: selectedBu || localStorage.getItem('bu_name'),
    brand: selectedFilters?.['brand'] || null,
    // subcategory: null,
    // subBrand: null,
    segment: selectedFilters?.['segment'] || null,
    // skuName: null,
    region: selectedFilters?.['region'] || null,
    channel: selectedFilters?.['channel'] || null,
    // customer: null,
    timeframe: timeFrameValue,
    subcategory: selectedFilters?.['subcategory'] || null
  };

  const filterConfigs = [
    {
      label: 'Brand',
      items: ['Option A', 'Option B', 'Option C'],
      key: 'brand'
    },
    {
      label: 'Sub-Brand',
      items: ['Active', 'Inactive', 'Pending'],
      key: 'subBrand'
    },
    {
      label: 'Segment',
      items: ['Option 1', 'Option 2', 'Option 3'],
      key: 'segment'
    },
    {
      label: 'SKU Name',
      items: ['Option A', 'Option B', 'Option C'],
      key: 'skuName'
    },
    {
      label: 'Region',
      items: ['Option A', 'Option B', 'Option C'],
      key: 'region'
    },
    {
      label: 'Channel',
      items: ['Option A', 'Option B', 'Option C'],
      key: 'channel'
    }
  ];

  const filtersConfig = [
    { key: 'segment', label: 'Segment', dependsOn: [] },
    { key: 'subcategory', label: 'Sub Category', dependsOn: ['segment'] },
    { key: 'brand', label: 'Brand', dependsOn: ['segment', 'subcategory'] },
    { key: 'region', label: 'Region', dependsOn: [] },
    { key: 'channel', label: 'Channel', dependsOn: [] }
  ];

  useEffect(() => {
    const fetchData = async () => {
      setShowLoader(true);
      try {
        const response = await fetchCurrentTimeFrame(selectedBu, timeFrameValue);

        setTimeFrame({
          startTimePeriod: response?.startTimeFrame || 'NA',
          endTimePeriod: response?.endTimeFrame || 'NA'
        });
      } catch (error) {
        console.error('Error fetching timeframe data:', error);
        setTimeFrame({ startTimePeriod: 'NA', endTimePeriod: 'NA' });
      } finally {
        setShowLoader(false);
      }
    };

    if (selectedBu) {
      fetchData();
    }
  }, [selectedBu, timeFrameValue]);

  const getActiveCycleData = useMemo(
    () => async (selectedBu) => {
      try {
        const content = await fetchCycleTriggerData(selectedBu);
        setCyclesData(content);
        const completedCycles =
          content?.filter((item) => item.status === 'Completed' || item.status === 'Locked') || [];
        const defaultCycle =
          completedCycles.find((cycle) => cycle.activeStatus) || completedCycles[0];
        // Update local state and dispatch actions
        setActiveCycleData(
          completedCycles?.map((item) => ({ id: item.id, cycleName: item.cycleName }))
        );
        setActiveCycleId(defaultCycle?.id);
      } catch (error) {
        console.error('Error fetching data:', error);
        setActiveCycleData([]);
      }
    },
    [selectedBu]
  );

  // Debounce the getActiveCycleData function
  const debouncedGetActiveCycleData = useCallback(
    debounce(getActiveCycleData, 300), // 300ms delay
    [getActiveCycleData]
  );

  useEffect(() => {
    if (!isInvalidValue(selectedBu)) {
      debouncedGetActiveCycleData(selectedBu);
    }
  }, [debouncedGetActiveCycleData, selectedBu]);

  const handleAnalysisFilterChange = (e) => {
    const filterName = e.target.value;
    const keyFromValue = getKeyByValue(trendDropdownMapping, filterName);
    dispatch(
      setPortfolioSelectedFiltersData((prev) => ({ ...prev, trendAnalysisBrekup: keyFromValue }))
    );
    setSelectedTrendAnalysis(filterName);
    setValueLabel(keyFromValue);
  };

  const handlePerfromanceBreakdownFilterChange = (e) => {
    const filterName = e.target.value;
    const keyFromValue = getKeyByValue(performanceBreakdownMapping, filterName);
    setSelectedPerformanceBreakdownFilter(filterName);
    setPerformanceBreakdownLabel(keyFromValue);
  };

  const handleTimeframeChange = (e) => {
    const filterName = e.target.value;
    setTimeFrameValue(filterName);
  };

  const handleCycleChange = (event) => {
    const foundSelectedCycle = activeCycleData.find((cycle) => cycle.id === event.target.value);
    if (foundSelectedCycle) {
      setActiveCycleId(foundSelectedCycle.id);
    }
  };

  const convertToDollarLabel = (dataList) => {
    if (!Array.isArray(dataList)) {
      const num = parseFloat(dataList?.toString().replace(/,/g, ''));
      return `${num.toFixed(2)}`;
    }
    return dataList.map(convertToDollarLabel);
  };

  const handleApplyFilters = (selectedValues) => {
    triggerNotification(
      'Hold tight! Applying filter combinations might take some time to load',
      'info',
      { duration: 3000 }
    );
    setSelectedFilters(selectedValues);
  };

  const handleResetFilters = () => {
    triggerNotification('Hold tight! Resetting all data might take some time to load', 'info', {
      duration: 3000
    });
    setSelectedFilters([]);
  };

  const fetchInitialData = useCallback(async (params) => {
    // Set loading states
    setIsKPICardLoading(true);
    setIsLineChartLoading(true);
    setIsBarChartLoading(true);
    setIsWaterfallChartLoading(true);

    // Fetch KPI Data
    fetchPortfolioHealthKPIAndChartsData(params)
      .then(([kpiData]) => {
        setActualVsPreviousKPIData(kpiData);
        setIsKPICardLoading(false);
      })
      .catch(() => setIsKPICardLoading(false));

    // Fetch Line Chart Data
    fetchLineChartData(params)
      .then((lineData) => {
        const previousList = convertToDollarLabel(lineData?.datasets?.[0]?.data);
        const currentList = convertToDollarLabel(lineData?.datasets?.[1]?.data);

        setLineChartData({
          labels: lineData?.labels,
          datasets: [
            { label: 'Previous Period', data: previousList },
            { label: 'Actual Revenue', data: currentList }
          ]
        });
        setIsLineChartLoading(false);
      })
      .catch(() => setIsLineChartLoading(false));

    // Fetch Bar Chart Data
    fetchBarChartData(params)
      .then((barData) => {
        const previousBarList = convertToDollarLabel(barData?.datasets?.[0]?.data);
        const currentBarList = convertToDollarLabel(barData?.datasets?.[1]?.data);

        setBarChartData({
          labels: barData?.labels,
          datasets: [
            { label: 'Previous Period', data: previousBarList },
            { label: 'Actual Revenue', data: currentBarList }
          ]
        });
        setIsBarChartLoading(false);
      })
      .catch(() => setIsBarChartLoading(false));

    // Fetch Waterfall Chart Data
    getWaterfallChartData(params)
      .then((waterfallData) => {
        setWaterfallChartData(waterfallData);
        setIsWaterfallChartLoading(false);
      })
      .catch(() => setIsWaterfallChartLoading(false));
  }, []);

  const fetchWaterfallChartOnPerformanceChange = useCallback(async (params) => {
    try {
      setIsWaterfallChartLoading(true);
      const content = await getWaterfallChartData(params);
      setWaterfallChartData(content);
    } catch (error) {
      console.error('Error fetching waterfall chart data on performance change:', error);
    } finally {
      setIsWaterfallChartLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedBu) {
      fetchWaterfallChartOnPerformanceChange({
        ...params,
        performanceBreakdown: getKeyByValue(
          performanceBreakdownMapping,
          selectedPerformanceBreakdownFilter
        )
      });
    }
  }, [
    selectedPerformanceBreakdownFilter,
    selectedBu,
    fetchWaterfallChartOnPerformanceChange,
    selectedTab
  ]);

  const fetchBarChartOnTabChange = useCallback(async (params) => {
    try {
      setIsBarChartLoading(true);

      const content = await fetchBarChartData(params);
      const previousBarList = await convertToDollarLabel(content?.datasets?.[0]?.data);
      const currentBarList = await convertToDollarLabel(content?.datasets?.[1]?.data);

      setBarChartData({
        labels: content?.labels,
        datasets: [
          { label: 'Previous Period', data: previousBarList },
          { label: 'Actual Revenue', data: currentBarList }
        ]
      });
    } catch (error) {
      console.error('Error fetching bar chart data on tab change:', error);
    } finally {
      setIsBarChartLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedBu) {
      fetchBarChartOnTabChange({ ...params, levelName: selectedTab });
    }
  }, [selectedTab, selectedBu, fetchBarChartOnTabChange]);

  const fetchLineChart = useCallback(async (params) => {
    try {
      setIsLineChartLoading(true);

      const content = await fetchLineChartData(params);

      if (content) {
        const previousList = await convertToDollarLabel(content?.datasets?.[0]?.data);
        const currentList = await convertToDollarLabel(content?.datasets?.[1]?.data);

        setLineChartData({
          labels: content?.labels,
          datasets: [
            { label: 'Previous Period', data: previousList },
            { label: 'Actual Revenue', data: currentList }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching line chart data:', error);
    } finally {
      setIsLineChartLoading(false);
    }
  }, []);

  const fetchBarChart = useCallback(async (params) => {
    try {
      setIsBarChartLoading(true);

      const content = await fetchBarChartData(params);

      if (content) {
        const previousBarList = await convertToDollarLabel(content?.datasets?.[0]?.data);
        const currentBarList = await convertToDollarLabel(content?.datasets?.[1]?.data);

        setBarChartData({
          labels: content?.labels,
          datasets: [
            { label: 'Previous Period', data: previousBarList },
            { label: 'Actual Revenue', data: currentBarList }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching bar chart data:', error);
    } finally {
      setIsBarChartLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedBu) {
      fetchLineChart({
        ...params,
        trendAnalysisBrekup: getKeyByValue(trendDropdownMapping, selectedTrendAnalysis)
      });
      fetchBarChart({
        ...params,
        trendAnalysisBrekup: getKeyByValue(trendDropdownMapping, selectedTrendAnalysis)
      });
    }
  }, [selectedTrendAnalysis, selectedBu, fetchLineChart, fetchBarChart]);

  // Consolidated fetch function for cycle and filter data
  const fetchCycleData = useCallback(async (selectedBu) => {
    if (!selectedBu) return;

    try {
      const cycleData = await fetchCycleTriggerData(selectedBu);
      const completedCycles = cycleData?.filter((item) => item.status === 'Completed') || [];
      const defaultCycle =
        completedCycles.find((cycle) => cycle.activeStatus) || completedCycles[0];

      setActiveCycleData(
        completedCycles?.map((item) => ({
          id: item.id,
          cycleName: item.cycleName
        }))
      );
      setActiveCycleId(defaultCycle?.id);
    } catch (error) {
      console.error('Error fetching cycle data:', error);
      setActiveCycleData([]);
    }
  }, []);

  const fetchFilterData = useCallback(async (selectedBu) => {
    if (!selectedBu) return;

    try {
      const filterData = await getDropdownListByFilterParam(selectedBu);
      setHeaderFilterData(filterData);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  }, []);

  const fetchMetadata = useCallback(
    async (selectedBu) => {
      if (!selectedBu) return;

      fetchCycleData(selectedBu);
      fetchFilterData(selectedBu);
    },
    [fetchCycleData, fetchFilterData]
  );

  // Initial load
  useEffect(() => {
    if (selectedBu) {
      fetchMetadata(selectedBu);
    }
  }, [selectedBu, fetchMetadata]);

  // Load cycle summary when cycle changes
  useEffect(() => {
    if (selectedBu && activeCycleId && userRole) {
      const urole = userRole?.[0];
      getUserActionSummary(activeCycleId, urole, userName)
        .then((data) => setCycleFilterData(data))
        .catch((error) => console.error('Error fetching cycle summary:', error));
    }
  }, [selectedBu, activeCycleId, userRole]);

  // Handle filter changes
  useEffect(() => {
    if (selectedBu) {
      const params = {
        trendAnalysisBrekup: getKeyByValue(trendDropdownMapping, selectedTrendAnalysis),
        performanceBreakdown: getKeyByValue(
          performanceBreakdownMapping,
          selectedPerformanceBreakdownFilter
        ),
        levelName: selectedTab,
        buName: selectedBu,
        brand: selectedFilters?.['brand'] || null,
        segment: selectedFilters?.['segment'] || null,
        region: selectedFilters?.['region'] || null,
        channel: selectedFilters?.['channel'] || null,
        subcategory: selectedFilters?.['subcategory'] || null,
        timeframe: timeFrameValue
      };
      fetchInitialData(params);
    }
  }, [selectedFilters, timeFrameValue, selectedBu, fetchInitialData]);

  return (
    <>
      <Stack gap="24px" sx={{ padding: '24px', width: '100%' }}>
        <PageHeader
          pageHeader={'Portfolio Health'}
          cardHeaderDetails={timeframe}
          showCycle={false}
          showBu={true}
          showTimeFrame={true}
          showLoader={showLoader}
        />

        <Box
          data-testid="box-portfolio-health"
          id="box-portfolio-health"
          gap={2}
          sx={{
            background: 'transparent',
            display: 'flex',
            flexDirection: 'column',
            gap: 1 // Adjust this value to increase or decrease the gap,
          }}>
          {filterConfigs && filterConfigs.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                gap: '4px',
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: '4px',
                  backgroundColor: 'transparent'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '3px'
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.3)'
                }
              }}>
              <ThemeProvider theme={theme}>
                <MultiSelectFilters
                  data={headerFilterData}
                  filtersConfig={filtersConfig}
                  onApplyFilters={handleApplyFilters}
                  onResetFilters={handleResetFilters}
                  isApplyButtonVisible={false}
                />
              </ThemeProvider>
            </Box>
          ) : (
            <></>
          )}
          {/* kpi card title */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            alignContent="center"
            gap={1}>
            <h4 className="portfolio-health-scenario-title">Actual vs Previous</h4>
            <Stack direction="row" alignItems="center" gap={2}>
              <SkuDetails selectedBu={selectedBu} />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="extraSmall" sx={{ color: '#616161', whiteSpace: 'nowrap' }}>
                  Timeframe
                </Typography>
                <FormControl variant="outlined" fullWidth={false} sx={{ margin: 0, width: 'auto' }}>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={timeFrameValue}
                    onChange={handleTimeframeChange}
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      width: 'auto',
                      minWidth: '15rem',
                      height: '32px',
                      margin: 0,
                      fontSize: '14px'
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200
                        }
                      }
                    }}>
                    {timeFrameOptions?.map((item, index) => (
                      <MenuItem value={item} key={`${index + 1}`}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </Stack>
          </Stack>

          <Stack justifyContent={'space-between'} direction={'row'} gap={2}>
            <KpiGrid
              metricsData={actualVsPreviousKPIData}
              width="100%"
              selectedPage={'portfoliohealth'}
              isKPICardLoading={isKPICardLoading}
              timeFrame={timeFrameValue}
            />
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            alignContent="center"
            gap={1}>
            <h4 className="portfolio-health-scenario-title">User Action Summary</h4>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="extraSmall" sx={{ color: '#616161', whiteSpace: 'nowrap' }}>
                Cycle
              </Typography>
              <FormControl variant="outlined" fullWidth={false} sx={{ margin: 0, width: 'auto' }}>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  data-testid="cycle-select"
                  value={activeCycleId || ''}
                  onChange={handleCycleChange}
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    width: 'auto',
                    minWidth: '15rem',
                    height: '32px',
                    margin: 0,
                    fontSize: '14px'
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200
                      }
                    }
                  }}>
                  {activeCycleData?.map((item, index) => (
                    <MenuItem value={item.id} key={`${index + 1}`}>
                      {item.cycleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </Stack>
          {activeCycleData.length > 0 ? (
            <Stack justifyContent={'space-between'} direction={'row'} gap={2}>
              <KpiGrid
                metricsData={cycleFilterData}
                width="100%"
                selectedPage={'masterData'}
                isKPICardLoading={isKPICardLoading}
                cyclesData={cyclesData}
                activeCycleId={activeCycleId}
              />
            </Stack>
          ) : (
            <Card>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mt: 2
                }}>
                <MobiledataOffIcon sx={{ fontSize: '40px', color: '#3A3A3A', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#3A3A3A' }}>
                  There are no cycles in this Business Unit
                </Typography>
              </Box>
            </Card>
          )}
          <Stack
            justifyContent={'space-between'}
            alignItems={'center'}
            direction={'row'}
            gap={2}
            flexWrap="wrap">
            <div>
              <h4 className="portfolio-health-scenario-title">Trend Analysis & Break Up</h4>
              <CustomTabs tabs={chartTabs} selectedTab={selectedTab} onTabChange={setSelectedTab} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="extraSmall" sx={{ color: '#616161', whiteSpace: 'nowrap' }}>
                View By
              </Typography>
              <FormControl variant="outlined" fullWidth={false} sx={{ margin: 0, width: 'auto' }}>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  data-testid="trend-analysis"
                  value={selectedTrendAnalysis}
                  onChange={handleAnalysisFilterChange}
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    width: 'auto',
                    minWidth: '15rem',
                    height: '32px',
                    margin: 0,
                    fontSize: '14px'
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200
                      }
                    }
                  }}>
                  {trendAnalysisOptions?.map((item, index) => (
                    <MenuItem value={item} key={`${index + 1}`}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <BarChart
                chartData={barChartData}
                title={`Net ${selectedTrendAnalysis} Per ${selectedTab}`}
                valueLabel={valueLabel}
                isBarChartLoading={isBarChartLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LineChart
                title={`Actual ${selectedTrendAnalysis} vs Previous Period`}
                chartData={lineChartData}
                valueLabel={valueLabel}
                isLineChartLoading={isLineChartLoading}
                timeframe = {timeframe}
              />
            </Grid>
          </Grid>

          <Stack justifyContent={'space-between'} alignItems={'center'} direction={'row'} gap={2}>
            <h4 className="portfolio-health-scenario-title">Performance Breakdown</h4>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="extraSmall" sx={{ color: '#616161', whiteSpace: 'nowrap' }}>
                View By
              </Typography>
              <FormControl variant="outlined" fullWidth={false} sx={{ margin: 0, width: 'auto' }}>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedPerformanceBreakdownFilter}
                  onChange={handlePerfromanceBreakdownFilterChange}
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    width: 'auto',
                    minWidth: '15rem',
                    height: '32px',
                    margin: 0,
                    fontSize: '14px'
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200
                      }
                    }
                  }}>
                  {performanceBreakdownOptions?.map((item, index) => (
                    <MenuItem value={item} key={`${index + 1}`}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </Stack>
          <Grid item>
            <WaterfallChart
              title={`Actual ${performanceBreakdownLabel} vs Previous Period`}
              chartData={waterfallChartData}
              valueLabel={performanceBreakdownLabel}
              isWaterfallChartLoading={isWaterfallChartLoading}
            />
          </Grid>
        </Box>
      </Stack>
    </>
  );
}

export default PortfolioHealth;
